import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Info, Video } from 'lucide-react';
import { VideoStage } from './VideoStage';
import { VideoFeatures } from '@/store/videoFeatures';
import { DeviceFrame, DEVICE_MAX_WIDTH, usePreviewDevice } from './DeviceFrame';
import { DevicePreviewToggle } from './DevicePreviewToggle';

type VideoStatus = 'empty' | 'uploading' | 'processing' | 'ready' | 'error';

interface ErrorState {
  type: 'format' | 'size' | 'network' | 'processing';
  message: string;
}

interface VideoPreviewSectionProps {
  features: VideoFeatures;
  setFeature: <K extends keyof VideoFeatures>(key: K, value: VideoFeatures[K]) => void;
  videoStatus?: VideoStatus;
  setVideoStatus?: (status: VideoStatus) => void;
  uploadProgress?: number;
  setUploadProgress?: (progress: number) => void;
  onReplaceVideo?: () => void;
  onCopyEmbed?: () => void;
  /** Nome exibido no cabeçalho do card de preview. */
  title?: string;
}

export function VideoPreviewSection({
  features,
  setFeature,
  videoStatus: externalVideoStatus,
  setVideoStatus: externalSetVideoStatus,
  uploadProgress: externalUploadProgress = 0,
  setUploadProgress: externalSetUploadProgress,
  onReplaceVideo,
  onCopyEmbed,
  title,
}: VideoPreviewSectionProps) {
  useLanguage();
  const [internalVideoStatus, setInternalVideoStatus] = useState<VideoStatus>(() =>
    features.videoUrl ? 'ready' : 'empty'
  );
  const [internalUploadProgress, setInternalUploadProgress] = useState(0);
  const [fileName, setFileName] = useState('');
  const [fileSize, setFileSize] = useState('');
  const [error, setError] = useState<ErrorState | null>(null);

  const videoStatus = externalVideoStatus ?? internalVideoStatus;
  const setVideoStatus = externalSetVideoStatus ?? setInternalVideoStatus;
  const uploadProgress = externalUploadProgress ?? internalUploadProgress;
  const setUploadProgress = externalSetUploadProgress ?? setInternalUploadProgress;

  const [previewDevice, setPreviewDevice] = usePreviewDevice();

  const handleReplaceVideo = () => {
    if (onReplaceVideo) {
      onReplaceVideo();
    } else {
      setFeature('videoUrl', '');
      setVideoStatus('empty');
      setUploadProgress(0);
      setError(null);
      setFileName('');
      setFileSize('');
    }
  };

  const handleCopyEmbed = () => {
    if (onCopyEmbed) {
      onCopyEmbed();
    }
  };

  const showDeviceControls = videoStatus === 'ready' && !!features.videoUrl;
  const activeDevice = showDeviceControls ? previewDevice : 'desktop';

  return (
    // min-h-full: ocupa o stage inteiro (o pai tem altura definida e scroll
    // próprio), com o card alinhado ao topo — não centrado verticalmente.
    <section className="flex min-h-full w-full items-start justify-center bg-[var(--editor-stage)] p-4 lg:p-6">
      {/* max-w-[960px] é hoje neutralizado pela regra global `div { max-width: 100vw }`
          do index.css (mesmo comportamento do front-host-dashboard, onde o card
          também renderiza full-width) — fica como teto de segurança caso a regra
          global seja escopada no futuro. */}
      <div className="flex w-full max-w-[960px] flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        {/* Cabeçalho do card: ícone + nome do vídeo + seletor de dispositivo */}
        <div className="flex min-h-[64px] items-center justify-between gap-4 px-5 py-3 lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Video className="h-[18px] w-[18px]" aria-hidden="true" />
            </div>
            <h1 className="truncate text-sm font-semibold leading-tight text-foreground">
              {title || translateInterfaceText("Vídeo sem título")}
            </h1>
          </div>
          {showDeviceControls && (
            <DevicePreviewToggle value={previewDevice} onChange={setPreviewDevice} />
          )}
        </div>

        {/* Player / moldura do dispositivo */}
        <div className="flex flex-1 items-start justify-center px-4 pb-4 lg:px-6 lg:pb-5">
          <div
            className="w-full mx-auto transition-[max-width] duration-300 ease-out"
            style={{ maxWidth: showDeviceControls ? DEVICE_MAX_WIDTH[previewDevice] : undefined }}
          >
            <DeviceFrame device={activeDevice}>
              <div className={activeDevice === 'desktop' ? 'overflow-hidden rounded-lg bg-background' : undefined}>
                <VideoStage
                  features={features}
                  setFeature={setFeature}
                  videoStatus={videoStatus}
                  setVideoStatus={setVideoStatus}
                  uploadProgress={uploadProgress}
                  setUploadProgress={setUploadProgress}
                  onReplaceVideo={handleReplaceVideo}
                  onCopyEmbed={handleCopyEmbed}
                  fileName={fileName}
                  fileSize={fileSize}
                  error={error}
                  setError={setError}
                />
              </div>
            </DeviceFrame>
          </div>
        </div>

        <footer className="flex min-h-[42px] items-center gap-2 px-5 pb-4 text-[11px] font-medium text-muted-foreground lg:px-6 lg:pb-5">
          <Info className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="truncate">{translateInterfaceText("Esta é uma visualização de como seu vídeo será exibido para o público.")}</span>
        </footer>
      </div>
    </section>
  );
}
